/**
 * Purchase Transaction Detail Page Component
 * /admin/purchases/:id
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import apiClient from '../api/client';

const PurchaseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [purchase, setPurchase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadPurchase();
  }, [id]);

  const loadPurchase = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient.getVehiclePurchase(id);
      setPurchase(data);
    } catch (err) {
      setError(err.message || 'Failed to load purchase transaction');
    } finally {
      setLoading(false);
    }
  };

  const getBuyerTypeLabel = (type) => {
    const labels = {
      investor: 'Investor',
      driver: 'Driver',
    };
    return labels[type] || type;
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      paid: 'success',
      partial: 'info',
      overdue: 'error',
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !purchase) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Purchase transaction not found'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/purchases')}>
          Back to Purchases
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/purchases')}>
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Purchase Transaction Details
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/admin/purchases/${id}/edit`)}
        >
          Edit
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Transaction Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Transaction Date
                </Typography>
                <Typography variant="body1">
                  {purchase.transaction_date
                    ? new Date(purchase.transaction_date).toLocaleDateString()
                    : '-'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Payment Status
                </Typography>
                <Chip
                  label={purchase.payment_status || 'pending'}
                  color={getPaymentStatusColor(purchase.payment_status)}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Vehicle
                </Typography>
                <Typography variant="body1">
                  {purchase.vehicle_plate_number || purchase.vehicle || '-'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Purchase Amount
                </Typography>
                <Typography variant="body1">
                  {purchase.purchase_amount
                    ? `${purchase.purchase_amount.toLocaleString()} AED`
                    : '-'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Buyer Type
                </Typography>
                <Typography variant="body1">
                  {purchase.buyer_type ? getBuyerTypeLabel(purchase.buyer_type) : '-'}
                </Typography>
              </Grid>
              {purchase.buyer_investor && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Buyer (Investor)
                  </Typography>
                  <Typography variant="body1">
                    {purchase.buyer_investor_name || purchase.buyer_investor}
                  </Typography>
                </Grid>
              )}
              {purchase.buyer_driver && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Buyer (Driver)
                  </Typography>
                  <Typography variant="body1">
                    {purchase.buyer_driver_name || purchase.buyer_driver}
                  </Typography>
                </Grid>
              )}
              {purchase.ar_transaction_id && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    AR Transaction ID
                  </Typography>
                  <Typography variant="body1">{purchase.ar_transaction_id}</Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default PurchaseDetail;

